<template>
    <div class="home-projects">
        <header class="toolbar">
            <div class="view-switch">
                <div class="view-switch-box">
                    <button class="item" :class="{active: activeViewMode===viewModeGrid}"
                            @click="setViewMode(viewModeGrid)"><i class="gf gf-gridview"></i></button>
                    <button class="item" :class="{active: activeViewMode===viewModeList}"
                            @click="setViewMode(viewModeList)"><i class="gf gf-listview"></i></button>
                </div>
            </div>
            <div class="search-container">
                <div class="search-box">
                    <input placeholder="Search projects..." @input="onProjectSearchInput" autofocus
                           title="Type to filter project name..." ref="searchField">
                    <span class="clear-icon" v-if="filterQuery" @click="clearSearchField"><i
                            class="gf gf-close-hover"></i></span>
                    <span class="search-icon"><i class="gf gf-search"></i></span>
                </div>
            </div>
        </header>
        <div class="main">
            <template v-if="showingResults">
                <template v-if="shownProjectsData.length">
                    <div class="projects-view projects-view-grid" v-if="activeViewMode===viewModeGrid">
                        <div class="items">
                            <div class="item" v-for="(item,index) in shownProjectsData" :key="index">
                                <div class="item-content" @click="enterProject(item['project_id'])">
                                    <div class="description">
                                        <div class="name" :title="item['project_name']">
                                            {{ item['project_name'] }}
                                        </div>
                                        <div class="modified">
                                            <span v-if="item['last_saved_time']"
                                                  :title="describeUsername(item['last_saved_by']) + ' (' + describeUnixTs(item['last_saved_time']) + ')'">
                                                Last saved by <span>{{ describeUsername(item['last_saved_by']) }}</span>
                                                ({{ describeUnixTs(item['last_saved_time'], '', true) }})
                                            </span>
                                            <span v-else>
                                                No Saved History
                                            </span>
                                        </div>
                                    </div>
                                    <div class="preview">
                                        <docs-status-ring :counts="item['count_of_documents']"></docs-status-ring>
                                    </div>
                                    <div class="footer">
                                        <div class="note">
                                            {{ numberWithCommas(item['count_of_documents']['total']) }} documents
                                        </div>
                                    </div>
                                    <div class="badge open-doc-count"
                                         v-if="getOpenDocCountInProject(item['project_id'])">
                                        {{ getOpenDocCountInProject(item['project_id']) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="projects-view projects-view-list" v-else-if="activeViewMode===viewModeList">
                        <div class="table-container">
                            <table class="standard-table">
                                <thead>
                                <tr>
                                    <th class="text-left">Project Name</th>
                                    <th class="text-right">Documents</th>
                                    <th class="text-left">Last Saved</th>
                                    <th>Progress</th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(item,index) in shownProjectsData" :key="index">
                                    <td class="text-left">
                                        <div>
                                            <button class="pure project-entry"
                                                    @click="enterProject(item['project_id'])">{{ item['project_name'] }}
                                            </button>
                                        </div>
                                    </td>
                                    <td class="text-right">
                                        <div>
                                            {{ numberWithCommas(item['count_of_documents']['total']) }}
                                            <span class="note"
                                                  v-if="getOpenDocCountInProject(item['project_id'])">{{
                                                    getOpenDocCountInProject(item['project_id'])
                                                }}</span>
                                        </div>
                                    </td>
                                    <td class="text-left">
                                        <div class="text-nowrap">
                                            <template v-if="item['last_saved_time']">
                                                {{ describeUnixTs(item['last_saved_time']) }}
                                                (by <span>{{ describeUsername(item['last_saved_by']) }}</span>)
                                            </template>
                                            <span v-else>
                                                Never
                                            </span>
                                        </div>
                                    </td>
                                    <td data-field="progress">
                                        <docs-status-bar :counts="item['count_of_documents']"></docs-status-bar>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                        <div class="legends">
                            <div><i class="bg-home-done"></i> Done</div>
                            <div><i class="bg-home-skipped"></i> Skipped</div>
                            <div><i class="bg-home-todo"></i> Todo</div>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="global-message-box">
                        <div>No projects<span v-if="filterQuery"> found for <strong>{{ filterQuery }}</strong></span>.
                        </div>
                    </div>
                </template>
            </template>
            <template v-else-if="isLoading">
                <div class="global-message-box">
                    <spinner></spinner>
                </div>
            </template>
            <template v-else-if="isHavingProblemLoading">
                <div class="global-message-box">
                    <span>There was something not right while loading projects.</span>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import {getProjectsStatistics} from "@/apis";
import {projectIdToName} from "@/config";
import DocsStatusBar from "@/components/DocsStatusBar";
import DocsStatusRing from "@/components/DocsStatusRing";
import {durablePreferences, numberWithCommas} from "@/utils"
import Spinner from "@/components/Spinner";

const viewModeGrid = 'grid';
const viewModeList = 'list';

export default {
    name: "HomeProjects",
    components: {Spinner, DocsStatusRing, DocsStatusBar},
    data() {
        return {
            viewModeGrid: viewModeGrid,
            viewModeList: viewModeList,
            data: null,
            isLoading: false,
            isHavingProblemLoading: false,
            showingResults: false,
            filterQuery: '',
            defaultViewMode: durablePreferences.getHomeViewMode() || viewModeGrid,
        }
    },
    computed: {
        shownProjectsData() {
            if (!this.data) {
                return [];
            }
            if (!this.filterQuery) {
                return this.data;
            }
            return this.data.filter(v => {
                return (v.project_name.toLowerCase().indexOf(this.filterQuery.toLowerCase()) !== -1);
            })
        },
        activeViewMode() {
            const q = this.$route.query.v;
            if (q === viewModeList || q === viewModeGrid) {
                return q;
            }
            return this.defaultViewMode;
        },
        openGlobalDocumentIds() {
            return this.$store.state.openGlobalDocumentIds
        },
    },
    methods: {
        numberWithCommas: numberWithCommas,
        clearSearchField() {
            this.$refs.searchField.value = '';
            this.filterQuery = '';
            this.$refs.searchField.focus();
        },
        setViewMode(mode) {
            if (this.activeViewMode === mode) {
                return;
            }

            this.$store.commit('setHomeLastSelectedViewMode', mode);
            durablePreferences.setHomeViewMode(mode);
            this.defaultViewMode = mode;

            this.$router.push({name: 'home', query: {v: mode}})
        },
        onProjectSearchInput(event) {
            this.filterQuery = event.currentTarget.value.trim();
        },
        setLoadingStatus(isLoading, isHavingProblemLoading, showingResults) {
            this.isLoading = isLoading;
            this.isHavingProblemLoading = isHavingProblemLoading;
            this.showingResults = showingResults;
        },
        loadProjects() {
            this.setLoadingStatus(true, false, false);
            getProjectsStatistics().then(r => {
                this.data = r.data.map(v => {
                    v.project_name = projectIdToName[v.project_id];
                    return v;
                });
                this.setLoadingStatus(false, false, true);
            }).catch((err) => {
                this.setLoadingStatus(false, true, false);
                this.handleServerSideError(err);
            })
        },
        loadProjectsSilently() {
            getProjectsStatistics().then(r => {
                this.data = r.data.map(v => {
                    v.project_name = projectIdToName[v.project_id];
                    return v;
                });
            }).catch((err) => {
                this.handleServerSideError(err);
            })
        },
        enterProject(projectId) {
            this.$router.push({name: 'document_finder', params: {pid: projectId}});
        },
        getOpenDocCountInProject(projectId) {
            return (this.openGlobalDocumentIds[projectId] || []).length;
        }
    },
    created() {
        this.loadProjects();
    },
    watch: {
        $route(newVal, oldVal) {
            if (newVal.name === 'home' && oldVal.name !== 'home') {
                this.loadProjectsSilently();
            }
        }
    }
}
</script>

<style scoped lang="scss">
@import "../assets/variables";

.view-switch-box {
    display: flex;
    align-items: center;

    .item {
        margin: 0;
        padding: 3px 8px;
        position: relative;
        box-shadow: none;
        display: inline-flex;
        align-items: center;

        &.active {
            background-color: $primary-purple;
            color: white;
            cursor: default;
        }

        &:not(:last-child) {
            border-bottom-right-radius: 0;
            border-top-right-radius: 0;
            border-right: none;
        }

        &:not(:first-child) {
            border-bottom-left-radius: 0;
            border-top-left-radius: 0;
            border-left: none;
        }
    }
}

.toolbar {
    display: flex;
    align-items: center;
    padding: 0 5rem 0 2rem;
    height: 90px;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    background-color: rgba(#f3f3f3, 0.6);

    & > * {
        &:not(:last-child) {
            margin-right: 0.8rem;
        }

        &:not(:first-child) {
            margin-left: 0.8rem;
        }
    }
}

.home-projects {
    height: 100%;
    position: relative;
}

.search-container {
    flex: 0 1 360px;

    .search-box {
        position: relative;

        .search-icon {
            position: absolute;
            left: 0.9rem;
            top: 1px;
            font-size: 1.2rem;
            line-height: 2.5rem;
            height: 2.5rem;
            opacity: 0.32;
        }

        .clear-icon {
            position: absolute;
            right: 0.75rem;
            top: 1px;
            font-size: 1.1em;
            line-height: 2.5rem;
            height: 2.5rem;
            opacity: 0.32;

            &:hover {
                opacity: 0.8;
            }
        }

        input {
            height: 2.5rem;
            width: 100%;
            padding-left: 2.5rem;
            padding-right: 2rem;
            border-radius: 999px;
            line-height: 1rem;
            border: 1px solid #f0f0f0;

            &:focus {
                box-shadow: 0 0 0 3px rgba($primary-blue, 0.5);
            }
        }
    }
}

.main {
    height: 100%;
    overflow: auto;
    padding: 90px 1rem 0;
}

.projects-view {
    height: 100%;

    &.projects-view-grid {
        .items {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-around;

          &::after {
            content: '';
            flex: auto;
          }

          .item {
            width: 100%;
            margin: 1.5rem 1.5rem;

            .item-content {
              position: relative;
              background: white;
              border-radius: 12px;
              width: 100%;
              height: 100%;
              cursor: pointer;
              transition: box-shadow 0.3s ease;
              border: 1px solid #f0f0f0;

                    .open-doc-count {
                        position: absolute;
                        top: 0;
                        right: 0;
                        margin-top: -8px;
                        margin-right: -10px;
                    }

                    &:hover {
                        box-shadow: 0 5px 18px -1px rgba(black, 0.1);
                    }

                    .preview {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        text-transform: capitalize;
                        padding: 0 0.8rem 0.25rem;
                    }

                    .footer {
                        height: 30px;
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        .note {
                            color: #777777;
                        }
                    }

                    .description {
                        height: 6rem;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-direction: column;
                        padding: 0 0.8rem;

                        .name {
                            margin-bottom: 0.6rem;
                            font-size: 1.5rem;
                            font-weight: 600;
                            white-space: nowrap;
                            overflow: hidden;
                            max-width: 100%;
                            text-overflow: ellipsis;
                            letter-spacing: 1px;
                        }

                        .modified {
                            margin-bottom: 0.5rem;
                            color: #333333;
                            max-width: 100%;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }
                    }
                }
            }

            @media all and (min-width: 800px) {
                .item {
                  width: calc(100% / 2 - 3rem);
                }
            }

            @media all and (min-width: 1400px) {
                .item {
                  width: calc(100% / 3 - 3rem);
                }
            }

            @media all and (min-width: 1800px) {
                .item {
                  width: calc(100% / 4 - 3rem);
                }
            }
        }
    }

    &.projects-view-list {
        .project-entry {
            font-size: 1rem;
            font-weight: 500;

            &:hover {
                color: $primary-purple;
                text-decoration: underline;
            }
        }

        .table-container {
            height: calc(100% - 2.5rem);
            overflow: auto;

            .project-name-box {
                position: relative;
                display: inline-block;

                .open-doc-count {
                    top: 0;
                    right: 0;
                    margin-top: -8px;
                    margin-right: -10px;
                }
            }
        }
    }
}

table {
    .note {
        &:before {
            content: '- ';
        }

        &:after {
            content: ' Open'
        }

        color: #555555;
        font-weight: 300;
    }

    td {

        &[data-field="progress"] {
            min-width: 250px;
        }
    }

    tr {

        &:hover {
            .project-entry {
                text-decoration: underline;
            }
        }
    }
}
</style>
